/**
 * Shared app-header links.
 *
 * BV (dev-shell), BV Express (dev-shell-express), the comparator report and the `:3030`
 * home page all render the same header bar (appHeader.css): a Home button, the page title,
 * and a BV Express / Comparator / BV Legacy segmented control that switches between the apps while
 * keeping the current course (`exportId`) and screen (`screenId`).
 *
 * This file is deliberately plain JavaScript with only `export function` / `export var`
 * declarations and no imports: the React shells import it as an ES module, while the
 * comparator inlines it verbatim into report.html (report.js strips the `export` keywords)
 * and the home page loads it as a module script from the static server.
 *
 * URL layouts understood (the sibling app is derived from the current path):
 *
 *   local static server (packages/ served on :3030)
 *     /myLibrary/index.html                         home page (My Library, the Home button target)
 *     /index.html                                   workspace page (open by id, every local course)
 *     /dev-shell/dist/index.html                    BV
 *     /dev-shell-express/dist/index.html            Express
 *     /epub-reader/dist/index.html                  EPUB (VegaReader, packages/epub-reader; local only)
 *     /comparator/dist/index.html?exportId=<id>     comparator viewer (packages/comparator/dist)
 *     /courses/comp-webp/comp-<id>/                 a comparison's data, kept with the courses
 *
 *   hosted dev shells, at the root or inside a deployment folder (`yarn uat` builds
 *   uat/uat-<date>/ holding dev_shell/dist, dev_shell_express/dist, myLibrary and the comparator
 *   viewer side by side; the folder, at any depth, is the prefix of every path)
 *     [/<folder>]/dev_shell/<segment>/index.html              BV
 *     [/<folder>]/dev_shell_express/<segment>/index.html      Express
 *     [/<folder>]/myLibrary/index.html                        home page (My Library)
 *     [/<folder>]/comparator/dist/index.html?exportId=<id>    comparator viewer
 *     /comp-webp/comp-<id>/                                   a comparison's data, at the root
 *                                                             with the course packages (/<id>/)
 *   (seen from the viewer or the library page, the shells are assumed at <app>/dist/)
 *
 * Anything else (vite dev server, file://) gets disabled tabs and no Home link.
 *
 * The Comparator tab is only shown when the course's comparison data actually exists:
 * callers probe `comparatorDataUrl(...) + "statistics.json"` with `probeComparatorReport`
 * (a HEAD request) and pass the answer in as `reportExists`; otherwise the tab is left out of
 * the bar entirely. `comparatorReportUrl` is the viewer opened on that course.
 */

/** Tab order and labels, shared by every renderer of the header. */
export var APP_HEADER_TABS = [
  { key: "express", label: "BV Express" },
  { key: "comparator", label: "Comparator" },
  { key: "bv", label: "BV Legacy" },
  { key: "epub", label: "EPUB" },
];

/**
 * Paths inside the layout's folder ("/" locally and for root-level hosted shells, the
 * deployment folder otherwise): where the Home button lands (the My Library page,
 * packages/myLibrary/index.html) and the comparator viewer (packages/comparator/dist), which
 * shows one comparison per `?exportId=` the way the shells show one course.
 */
var HOME_PATH = "myLibrary/index.html";
var COMPARATOR_VIEWER_PATH = "comparator/dist/index.html";
/**
 * A comparison's data (statistics.json, diff/) is stored with the course packages, not with
 * the apps: /courses/comp-webp/comp-<exportId>/ on the local server (packages/ served on a
 * port) and /comp-webp/comp-<exportId>/ at the root of a hosted origin - the same split the
 * shells' getURLParams applies to the packages themselves (/courses/<id>/ vs /<id>/).
 * Hosted /comp/ holds the PNG-era data the earlier UAT folders (sep-28 .. oct-09) were
 * built against; it stays frozen for them, so WebP-tiled data gets its own root.
 */
var COMPARATOR_DATA_PATH = "/comp-webp/";
var LOCAL_DATA_PREFIX = "/courses";
/** The shells keep their package names on the local server ... */
var LOCAL_SHELL_PATHS = {
  bv: "/dev-shell/dist/index.html",
  express: "/dev-shell-express/dist/index.html",
  epub: "/epub-reader/dist/index.html",
};
/** ... and use the bucket's folder names when hosted, followed by a segment (dist/, <user>/, ...). */
var HOSTED_SHELL_FOLDERS = { bv: "dev_shell/", express: "dev_shell_express/" };
/** Segment assumed when the page is not itself a shell: `yarn uat` deploys the shells as <app>/dist/. */
var HOSTED_DEFAULT_SEGMENT = "dist/";

/** Query params copied from the current URL onto the sibling shell links. */
var CARRIED_PARAMS = ["env", "packagePath"];

/**
 * Whether two apps route by different screen ids for one course: EPUB screen ids are its xhtml
 * page names (one page per lesson), the shells route by the INTERACTIVE screens.
 * @param {string} rendererA
 * @param {string} rendererB
 */
function crossesEpub(rendererA, rendererB) {
  return (rendererA === "epub") !== (rendererB === "epub");
}

/** The key of an export seen through one renderer, for `matchedScreenIds`. */
function sideKey(exportId, renderer) {
  return exportId + "_" + renderer;
}

var HOME_ICON_SVG =
  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M2.5 7.6 8 2.9l5.5 4.7"/>' +
  '<path d="M4 6.9V13a.6.6 0 0 0 .6.6h6.8a.6.6 0 0 0 .6-.6V6.9"/>' +
  '<path d="M6.6 13.6V9.7h2.8v3.9"/>' +
  "</svg>";

/**
 * @typedef {{ kind: "local" | "hosted" | "unknown", prefix?: string, segment?: string }} Layout
 *   `prefix` is the folder holding the apps ("/" locally and for root-level hosted shells,
 *   "/uat/uat-2026-sep-28/" inside a deployment folder); on a hosted layout `segment` is the part
 *   between the shell's folder and its index.html.
 */

/**
 * @param {URL} url
 * @returns {Layout}
 */
function detectLayout(url) {
  var pathname = url.pathname;
  if (
    pathname === "/" ||
    pathname === "/index.html" ||
    pathname.indexOf("/myLibrary/") === 0 ||
    pathname.indexOf("/dev-shell/dist/") === 0 ||
    pathname.indexOf("/dev-shell-express/dist/") === 0 ||
    pathname.indexOf("/epub-reader/dist/") === 0 ||
    pathname.indexOf("/comparator/") === 0
  ) {
    return { kind: "local", prefix: "/" };
  }
  // /dev_shell/<user>/index.html, /uat/uat-2026-sep-28/dev_shell_express/dist/index.html, ... -
  // the folders before the shell's folder (possibly none) and the part between it and
  // index.html (possibly empty) are both kept for the sibling shell; the first shell folder on
  // the path wins
  var shell = pathname.match(
    /^((?:\/[^/]+)*?\/)(dev_shell|dev_shell_express)\/((?:[^/]+\/)*)index\.html$/,
  );
  if (shell) {
    return { kind: "hosted", prefix: shell[1], segment: shell[3] };
  }
  // the other pages of a deployment folder: /uat/uat-2026-sep-28/comparator/dist/index.html,
  // /uat/uat-2026-sep-28/myLibrary/index.html - their shells sit at <folder>/<app>/dist/
  var page = pathname.match(
    /^((?:\/[^/]+)*?\/)(?:comparator\/dist\/index\.html|myLibrary\/index\.html)$/,
  );
  if (page) {
    return { kind: "hosted", prefix: page[1], segment: HOSTED_DEFAULT_SEGMENT };
  }
  return { kind: "unknown" };
}

/**
 * @param {string} href
 * @returns {{ url: URL | null, origin: string | null, layout: Layout }}
 */
function parseLocation(href) {
  var url = null;
  try {
    url = new URL(href);
  } catch (e) {
    url = null;
  }
  var origin = url && /^https?:$/.test(url.protocol) ? url.origin : null;
  var layout = url && origin ? detectLayout(url) : { kind: "unknown" };
  return { url: url, origin: origin, layout: layout };
}

/**
 * A shell's index.html (no query) as seen from `location`: the package dist on the local server,
 * the shell's folder plus segment inside the same deployment folder when hosted.
 * @param {{ origin: string | null, layout: Layout }} location  a known layout
 * @param {"bv" | "express" | "epub"} key
 * @returns {string}
 */
function shellPageUrl(location, key) {
  var layout = location.layout;
  if (layout.kind === "local") {
    return (location.origin || "") + (LOCAL_SHELL_PATHS[key] || "");
  }
  var folder = HOSTED_SHELL_FOLDERS[key];
  if (!folder) {
    return "";
  }
  return location.origin + layout.prefix + folder + layout.segment + "index.html";
}

/** The same test as the shells' getURLParams: a port or a localhost name means packages/ is served locally. */
function servesPackagesLocally(url) {
  var hostname = url.hostname;
  return (
    Boolean(url.port) ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

/**
 * The folder holding a comparison's data, with a trailing slash: comp-<exportId>/ for an
 * Express-vs-BV run of one course, comp-<exportId>_<render1>_<exportId2>_<render2>/ for any
 * other two-export run (the comparator names it with the sides in comparison order). It
 * does not depend on the page's layout, only on whether packages are served locally, so the
 * viewer can be opened from anywhere. null for a non-http page, without a course, or for a
 * second export without both renderers.
 * @param {string} href
 * @param {string | undefined} exportId
 * @param {string | undefined} [exportId2]
 * @param {string | undefined} [render1]
 * @param {string | undefined} [render2]
 * @returns {string | null}
 */
export function comparatorDataUrl(href, exportId, exportId2, render1, render2) {
  var location = parseLocation(href);
  if (!location.origin || !exportId || (exportId2 && !(render1 && render2))) {
    return null;
  }
  var classic = !exportId2 || (exportId2 === exportId && render1 === "express" && render2 === "bv");
  var name = classic
    ? "comp-" + encodeURIComponent(exportId)
    : "comp-" + [exportId, render1, exportId2, render2].map(encodeURIComponent).join("_");
  var dataRoot = servesPackagesLocally(location.url) ? LOCAL_DATA_PREFIX : "";
  return location.origin + dataRoot + COMPARATOR_DATA_PATH + name + "/";
}

/**
 * A course export's toc.json as an app sees it: /courses/<id>/ (Bronte) or /courses/epub/<id>/
 * (EPUB) when packages are served locally, /<id>/ at the root of a hosted origin - where the
 * shells' getURLParams and epub-reader look. null for a non-http page, without a course, or
 * for an EPUB export when hosted (only served locally).
 * @param {string} href
 * @param {string | undefined} exportId
 * @param {string} renderer
 * @returns {string | null}
 */
export function courseTocUrl(href, exportId, renderer) {
  var location = parseLocation(href);
  if (!location.origin || !exportId) {
    return null;
  }
  var local = servesPackagesLocally(location.url);
  if (renderer === "epub" && !local) {
    return null;
  }
  var folder = (local ? LOCAL_DATA_PREFIX : "") + (renderer === "epub" ? "/epub" : "");
  return location.origin + folder + "/" + encodeURIComponent(exportId) + "/toc.json";
}

/**
 * The exports, as `{ exportId, renderer }`, whose screen the header's tabs need matched by toc
 * (loadMatchedScreenIds) from a page of `app` showing `exportId`: with a ?ctx= the page is a
 * side of, its other side when that is another export or across the EPUB boundary; otherwise
 * the tabs across the EPUB boundary (the EPUB tab from a shell, the shells from EPUB).
 * @param {string} href
 * @param {string} app
 * @param {string | undefined} exportId
 * @returns {ComparisonSide[]}
 */
export function screenMatchTargets(href, app, exportId) {
  if (!exportId) {
    return [];
  }
  var context = comparisonContextFromHref(href);
  var own = context
    ? context.sides.filter(function (side) {
        return side.renderer === app && side.exportId === exportId;
      })[0]
    : undefined;
  if (context && own) {
    var other = context.sides[0] === own ? context.sides[1] : context.sides[0];
    return other.exportId !== exportId || crossesEpub(app, other.renderer) ? [other] : [];
  }
  return APP_HEADER_TABS.filter(function (tab) {
    return tab.key !== "comparator" && crossesEpub(app, tab.key);
  }).map(function (tab) {
    return /** @type {ComparisonSide} */ ({ exportId: exportId, renderer: tab.key });
  });
}

// an export id: a 32-hex id or a dashed UUID. Anchors the comp-<id1>_<r1>_<id2>_<r2> split
// ahead of the catch-all comp-<id>.
var EXPORT_ID_PATTERN = "[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
var RENDERER_PATTERN = "express|bv|epub";
var COMPARISON_NAME_RE = new RegExp(
  "^(?:comp-(" +
    EXPORT_ID_PATTERN +
    ")_(" +
    RENDERER_PATTERN +
    ")_(" +
    EXPORT_ID_PATTERN +
    ")_(" +
    RENDERER_PATTERN +
    ")|comp-(.+))$",
  "i",
);

/**
 * The viewer's ?exportId= / ?exportId2= / ?render1= / ?render2= params, also accepting a
 * comparison folder name (comp-<id> or comp-<id1>_<r1>_<id2>_<r2>, as listed under
 * courses/comp-webp/) in exportId alone. Anything else is returned as given.
 * @param {string} exportId
 * @param {string} [exportId2]
 * @param {string} [render1]
 * @param {string} [render2]
 * @returns {{ exportId: string, exportId2: string | undefined, render1: string | undefined, render2: string | undefined }}
 */
export function parseComparisonParams(exportId, exportId2, render1, render2) {
  var match = !exportId2 && COMPARISON_NAME_RE.exec(exportId);
  if (!match) {
    return {
      exportId: exportId,
      exportId2: exportId2 || undefined,
      render1: (exportId2 && render1) || undefined,
      render2: (exportId2 && render2) || undefined,
    };
  }
  if (match[1]) {
    return { exportId: match[1], exportId2: match[3], render1: match[2], render2: match[4] };
  }
  return { exportId: match[5], exportId2: undefined, render1: undefined, render2: undefined };
}

/**
 * A comparison context, `?ctx=<exportId1>_<renderer1>_<exportId2>_<renderer2>`: the two sides
 * My Library (or Bronte) asked to compare. Every app carries it on its links, and the header
 * then shows one tab per side with the Comparator between them instead of one tab per app.
 * The sides are kept in comparison order, as the comparator names its folders
 * (packages/comparator/src/naming.js orderSides): Express always left, EPUB always right, and
 * with the same renderer on both sides the newer export (by its uuid time) left.
 */
var CTX_PARAM = "ctx";
var RENDERER_RANK = { express: 0, bv: 1, epub: 2 };
var CTX_RE = new RegExp(
  "^(" +
    EXPORT_ID_PATTERN +
    ")_(" +
    RENDERER_PATTERN +
    ")_(" +
    EXPORT_ID_PATTERN +
    ")_(" +
    RENDERER_PATTERN +
    ")$",
  "i",
);
// 100ns ticks between the uuid epoch (1582-10-15) and the unix epoch
var UUID_EPOCH_OFFSET_MS = 12219292800000;

/**
 * Creation time of a version-1 uuid export id (dashed, or a bare 32-hex EPUB id) in 100ns
 * ticks, null for any other id.
 * @param {string} exportId
 * @returns {number | null}
 */
function exportIdTime(exportId) {
  var hex = String(exportId || "").replace(/-/g, "");
  if (!/^[0-9a-f]{32}$/i.test(hex) || hex.charAt(12) !== "1") {
    return null;
  }
  return parseInt(hex.slice(13, 16) + hex.slice(8, 12) + hex.slice(0, 8), 16);
}

/**
 * @typedef {{ exportId: string, renderer: "express" | "bv" | "epub" }} ComparisonSide
 * @typedef {{ key: string, sides: ComparisonSide[] }} ComparisonContext
 */

/**
 * A ctx value as a context with its sides in comparison order (`key` is the reordered value),
 * or null when it is malformed or names the same export and renderer twice.
 * @param {string | null | undefined} value
 * @returns {ComparisonContext | null}
 */
export function parseComparisonContext(value) {
  var match = CTX_RE.exec(String(value || "").trim());
  if (!match) {
    return null;
  }
  var a = { exportId: match[1], renderer: match[2].toLowerCase() };
  var b = { exportId: match[3], renderer: match[4].toLowerCase() };
  if (a.exportId === b.exportId && a.renderer === b.renderer) {
    return null;
  }
  var swap = RENDERER_RANK[a.renderer] > RENDERER_RANK[b.renderer];
  if (a.renderer === b.renderer) {
    var timeA = exportIdTime(a.exportId);
    var timeB = exportIdTime(b.exportId);
    swap = timeA !== null && timeB !== null && timeA < timeB;
  }
  var sides = swap ? [b, a] : [a, b];
  return {
    key: sides
      .map(function (side) {
        return side.exportId + "_" + side.renderer;
      })
      .join("_"),
    sides: /** @type {ComparisonSide[]} */ (sides),
  };
}

/**
 * The ?ctx= of a page URL, parsed (see parseComparisonContext).
 * @param {string} href
 * @returns {ComparisonContext | null}
 */
export function comparisonContextFromHref(href) {
  var location = parseLocation(href);
  return location.url ? parseComparisonContext(location.url.searchParams.get(CTX_PARAM)) : null;
}

/**
 * Where a context's comparison data lives (see comparatorDataUrl).
 * @param {string} href
 * @param {ComparisonContext} context
 * @returns {string | null}
 */
function contextDataUrl(href, context) {
  return comparatorDataUrl(
    href,
    context.sides[0].exportId,
    context.sides[1].exportId,
    context.sides[0].renderer,
    context.sides[1].renderer,
  );
}

/**
 * "Oct 1" for an export id's creation day, the id's first 8 characters without a uuid time.
 * @param {string} exportId
 */
function exportIdDay(exportId) {
  var time = exportIdTime(exportId);
  if (time === null) {
    return exportId.slice(0, 8);
  }
  return new Date(time / 10000 - UUID_EPOCH_OFFSET_MS).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Tab labels of a context's sides: the renderer's tab label, plus the export's creation day
 * (or, on the same day, its id) when both sides use the same renderer.
 * @param {ComparisonContext} context
 * @returns {string[]}
 */
function contextSideLabels(context) {
  var labels = context.sides.map(function (side) {
    var tab = APP_HEADER_TABS.filter(function (candidate) {
      return candidate.key === side.renderer;
    })[0];
    return tab ? tab.label : side.renderer;
  });
  var a = context.sides[0];
  var b = context.sides[1];
  if (a.renderer !== b.renderer) {
    return labels;
  }
  var days = [exportIdDay(a.exportId), exportIdDay(b.exportId)];
  if (days[0] === days[1]) {
    days = [a.exportId.slice(0, 8), b.exportId.slice(0, 8)];
  }
  return labels.map(function (label, index) {
    return label + " · " + days[index];
  });
}

/**
 * The comparator viewer in this page's folder, opened on the course (without a screen), or
 * null when the layout is unknown or there is no course. Whether the comparison exists is a
 * separate question - probe `comparatorDataUrl(...) + "statistics.json"`.
 * @param {string} href
 * @param {string | undefined} exportId
 * @returns {string | null}
 */
export function comparatorReportUrl(href, exportId) {
  var location = parseLocation(href);
  if (location.layout.kind === "unknown" || !exportId) {
    return null;
  }
  return withQuery(location.origin + location.layout.prefix + COMPARATOR_VIEWER_PATH, {
    exportId: exportId,
  });
}

/**
 * @type {Record<string, Promise<boolean>>} url -> exists. Only a found comparison is
 * remembered: on the hosted shells the first probe runs before the login has primed the
 * CloudFront cookies that guard the data, so a miss has to be asked again later.
 */
var reportProbes = {};

/**
 * Whether a comparison exists at `url` (its statistics.json): a HEAD request, one per URL
 * while it is pending or once it succeeded. Resolves false when there is no URL, no fetch,
 * or the request fails.
 * @param {string | null} url
 * @returns {Promise<boolean>}
 */
export function probeComparatorReport(url) {
  if (!url) {
    return Promise.resolve(false);
  }
  if (!reportProbes[url]) {
    var probe;
    try {
      probe =
        typeof fetch === "function"
          ? fetch(url, { method: "HEAD", cache: "no-store" }).then(function (response) {
              return response.ok;
            })
          : Promise.resolve(false);
    } catch (e) {
      probe = Promise.resolve(false);
    }
    reportProbes[url] = probe
      .catch(function () {
        return false;
      })
      .then(function (exists) {
        if (!exists) {
          delete reportProbes[url];
        }
        return exists;
      });
  }
  return reportProbes[url];
}

/** @type {Record<string, Promise<Record<string, string> | null>>} url -> side-1 -> side-2 screen ids */
var screenMapLoads = {};

/**
 * The screen pairs of the comparison at `url` (its statistics.json, which lists every paired
 * screen, compared or not): side-1 screenId -> side-2 screenId. A GET, one per URL while it
 * is pending or once it succeeded; null when the comparison is missing or unreadable.
 * @param {string | null} url
 * @returns {Promise<Record<string, string> | null>}
 */
export function loadComparatorScreenMap(url) {
  if (!url) {
    return Promise.resolve(null);
  }
  if (!screenMapLoads[url]) {
    var load;
    try {
      load =
        typeof fetch === "function"
          ? fetch(url, { cache: "no-store" }).then(function (response) {
              return response.ok ? response.json() : null;
            })
          : Promise.resolve(null);
    } catch (e) {
      load = Promise.resolve(null);
    }
    screenMapLoads[url] = load
      .then(function (stats) {
        if (!stats) {
          return null;
        }
        /** @type {Record<string, string>} */
        var map = {};
        (stats.chapters || []).forEach(function (chapter) {
          (chapter.lessons || []).forEach(function (lesson) {
            (lesson.screens || []).forEach(function (screen) {
              if (screen.screenId && screen.screenId2) {
                map[screen.screenId] = screen.screenId2;
              }
            });
          });
        });
        return map;
      })
      .catch(function () {
        return null;
      })
      .then(function (map) {
        if (!map) {
          delete screenMapLoads[url];
        }
        return map;
      });
  }
  return screenMapLoads[url];
}

/**
 * What the header needs to know about the page's comparison, for `resolveAppLinks`: whether
 * it exists and, for a ?ctx= over two different exports, its screen pairs. Without a ctx this
 * is the course's comp-<exportId> probe.
 * @param {string} href
 * @param {string | undefined} exportId
 * @returns {Promise<{ reportExists: boolean, screenMap: Record<string, string> | null }>}
 */
export function loadComparatorState(href, exportId) {
  var context = comparisonContextFromHref(href);
  var dataUrl = context ? contextDataUrl(href, context) : comparatorDataUrl(href, exportId);
  var statsUrl = dataUrl ? dataUrl + "statistics.json" : null;
  if (context && context.sides[0].exportId !== context.sides[1].exportId) {
    return loadComparatorScreenMap(statsUrl).then(function (map) {
      return { reportExists: Boolean(map), screenMap: map };
    });
  }
  return probeComparatorReport(statsUrl).then(function (exists) {
    return { reportExists: exists, screenMap: null };
  });
}

/**
 * Appends the truthy `params` to `base`, which may already carry a query.
 * @param {string} base
 * @param {Record<string, string | undefined | null>} params
 */
function withQuery(base, params) {
  var parts = [];
  Object.keys(params).forEach(function (name) {
    var value = params[name];
    if (value) {
      parts.push(encodeURIComponent(name) + "=" + encodeURIComponent(value));
    }
  });
  if (!parts.length) {
    return base;
  }
  return base + (base.indexOf("?") === -1 ? "?" : "&") + parts.join("&");
}

/**
 * @typedef {{ key: string, label: string, href: string | null, current: boolean, hidden?: boolean, reason?: string }} AppHeaderTab
 * @typedef {{ home: string | null, tabs: AppHeaderTab[] }} AppHeaderLinks
 */

/**
 * Resolves the Home link and the three tab links for the header.
 *
 * @param {{
 *   href: string,
 *   app: "bv" | "express" | "epub" | "comparator" | "home",
 *   exportId?: string,
 *   screenId?: string,
 *   matchedScreenIds?: Record<string, string>,
 *   reportExists?: boolean,
 *   ctx?: string,
 *   screenMap?: Record<string, string> | null,
 *   sideScreenIds?: (string | undefined)[],
 * }} options
 *   `href` is the current page URL (window.location.href). `app` is the app rendering the
 *   header (its tab is marked current and gets no link). `exportId` / `screenId` are what
 *   the other apps should open. `matchedScreenIds` holds the screen of `screenId` in the other
 *   exports or across the EPUB boundary, keyed `<exportId>_<renderer>` (loadMatchedScreenIds,
 *   which matches the tocs by name); a target missing from it gets no screen. `reportExists`
 *   is the answer of
 *   `loadComparatorState` / `probeComparatorReport` for this course - without a true here the
 *   Comparator tab is hidden.
 *
 *   With a comparison context (`ctx`, else the page's ?ctx=) that the page belongs to - the
 *   comparator, or the app and exportId of one of its sides - the tabs are the two sides with
 *   the Comparator between them, and every link carries the ctx. The other side's screen is
 *   the one from `matchedScreenIds`, else the same screenId for one export with the same ids,
 *   else the pair from `screenMap` (side-1 -> side-2 ids, from `loadComparatorState`), else
 *   none; `sideScreenIds` gives both sides' screens directly.
 * @returns {AppHeaderLinks}
 */
export function resolveAppLinks(options) {
  var location = parseLocation(options.href);
  var url = location.url;
  var origin = location.origin;
  var layout = location.layout;
  var carried = {};
  if (url) {
    CARRIED_PARAMS.forEach(function (name) {
      var value = url.searchParams.get(name);
      if (value) {
        carried[name] = value;
      }
    });
  }
  // an unknown layout still gets a root Home: the vite dev server has nothing better
  var folder = layout.kind === "unknown" ? "/" : layout.prefix;
  var home = origin ? origin + folder + HOME_PATH : null;

  var context =
    options.ctx !== undefined
      ? parseComparisonContext(options.ctx)
      : comparisonContextFromHref(options.href);
  var sideIndex = -1;
  if (context) {
    context.sides.forEach(function (side, index) {
      if (sideIndex === -1 && side.renderer === options.app && side.exportId === options.exportId) {
        sideIndex = index;
      }
    });
  }
  if (context && (options.app === "comparator" || sideIndex !== -1)) {
    return {
      home: home,
      tabs: contextTabs(location, context, sideIndex, carried, options),
    };
  }

  var tabs = APP_HEADER_TABS.map(function (tab) {
    var current = tab.key === options.app;
    var target = { exportId: options.exportId, screenId: options.screenId };
    /** @type {AppHeaderTab} */
    var result = { key: tab.key, label: tab.label, href: null, current: current };
    if (current) {
      return result;
    }
    if (tab.key === "comparator") {
      // only offered when the course's comparison is known to exist (see probeComparatorReport)
      var reportUrl = comparatorReportUrl(options.href, target.exportId);
      if (options.reportExists === true && reportUrl) {
        result.href = withQuery(reportUrl, { screenId: target.screenId });
      } else {
        result.hidden = true;
      }
      return result;
    }
    if (layout.kind === "unknown" || (tab.key === "epub" && layout.kind !== "local")) {
      result.reason = tab.label + " is not reachable from this host";
      return result;
    }
    var params = {
      exportId: target.exportId,
      screenId: crossesEpub(options.app, tab.key)
        ? (options.matchedScreenIds || {})[sideKey(target.exportId || "", tab.key)]
        : target.screenId,
    };
    Object.keys(carried).forEach(function (name) {
      params[name] = carried[name];
    });
    result.href = withQuery(shellPageUrl(location, tab.key), params);
    return result;
  });

  return { home: home, tabs: tabs };
}

/**
 * Both sides' screens for a context's links: `sideScreenIds` when given, else the page's own
 * screen on its side and its match on the other (see resolveAppLinks).
 * @param {ComparisonContext} context
 * @param {number} sideIndex  the page's side, -1 for the comparator
 * @param {{ screenId?: string, matchedScreenIds?: Record<string, string>, screenMap?: Record<string, string> | null, sideScreenIds?: (string | undefined)[] }} options
 * @returns {(string | undefined)[]}
 */
function contextScreenIds(context, sideIndex, options) {
  if (options.sideScreenIds) {
    return options.sideScreenIds;
  }
  var own = options.screenId || undefined;
  if (!own || sideIndex === -1) {
    return [undefined, undefined];
  }
  var other = context.sides[1 - sideIndex];
  var matched = (options.matchedScreenIds || {})[sideKey(other.exportId, other.renderer)];
  if (matched) {
    return sideIndex === 0 ? [own, matched] : [matched, own];
  }
  if (
    context.sides[0].exportId === context.sides[1].exportId &&
    !crossesEpub(context.sides[0].renderer, context.sides[1].renderer)
  ) {
    return [own, own];
  }
  var map = options.screenMap || {};
  if (sideIndex === 0) {
    return [own, map[own]];
  }
  var paired = Object.keys(map).filter(function (screenId) {
    return map[screenId] === own;
  })[0];
  return [paired, own];
}

/**
 * The tabs of a comparison context: side 1, Comparator, side 2.
 * @param {{ origin: string | null, layout: Layout }} location
 * @param {ComparisonContext} context
 * @param {number} sideIndex  the page's side, -1 for the comparator
 * @param {Record<string, string>} carried
 * @param {{ app: string, reportExists?: boolean, screenId?: string, matchedScreenIds?: Record<string, string>, screenMap?: Record<string, string> | null, sideScreenIds?: (string | undefined)[] }} options
 * @returns {AppHeaderTab[]}
 */
function contextTabs(location, context, sideIndex, carried, options) {
  var layout = location.layout;
  var labels = contextSideLabels(context);
  var screens = contextScreenIds(context, sideIndex, options);

  /** @param {number} index */
  function sideTab(index) {
    var side = context.sides[index];
    /** @type {AppHeaderTab} */
    var result = {
      key: "side" + (index + 1),
      label: labels[index],
      href: null,
      current: index === sideIndex,
    };
    if (result.current) {
      return result;
    }
    if (layout.kind === "unknown" || (side.renderer === "epub" && layout.kind !== "local")) {
      result.reason = labels[index] + " is not reachable from this host";
      return result;
    }
    /** @type {Record<string, string | undefined>} */
    var params = { exportId: side.exportId, screenId: screens[index], ctx: context.key };
    Object.keys(carried).forEach(function (name) {
      params[name] = carried[name];
    });
    result.href = withQuery(shellPageUrl(location, side.renderer), params);
    return result;
  }

  /** @type {AppHeaderTab} */
  var comparator = {
    key: "comparator",
    label: "Comparator",
    href: null,
    current: options.app === "comparator",
  };
  if (!comparator.current) {
    // only offered when the comparison is known to exist (see loadComparatorState); the viewer
    // keys its screens on side 1
    if (options.reportExists === true && layout.kind !== "unknown") {
      comparator.href = withQuery(
        (location.origin || "") + layout.prefix + COMPARATOR_VIEWER_PATH,
        { ctx: context.key, screenId: screens[0] },
      );
    } else {
      comparator.hidden = true;
    }
  }
  return [sideTab(0), comparator, sideTab(1)];
}

/**
 * Fills a Home anchor: href (hidden when there is none) and the house glyph.
 * @param {HTMLAnchorElement} anchor
 * @param {string | null} href
 */
export function setAppHeaderHome(anchor, href) {
  if (!anchor.firstElementChild) {
    anchor.innerHTML = HOME_ICON_SVG;
  }
  anchor.hidden = !href;
  if (href) {
    anchor.href = href;
  }
}

/**
 * Renders the segmented control into `container` (replacing its content). The React shells
 * render the same markup in AppHeader/index.tsx - keep the class names in sync.
 * @param {HTMLElement} container
 * @param {AppHeaderLinks} links
 */
export function renderAppHeaderTabs(container, links) {
  container.textContent = "";
  links.tabs.forEach(function (tab) {
    if (tab.hidden) {
      return;
    }
    var element;
    if (tab.current) {
      element = document.createElement("span");
      element.className = "app-header__tab is-current";
      element.setAttribute("aria-current", "page");
    } else if (tab.href) {
      element = document.createElement("a");
      element.className = "app-header__tab";
      element.href = tab.href;
      element.title = "Open in " + tab.label;
    } else {
      element = document.createElement("span");
      element.className = "app-header__tab is-disabled";
      element.setAttribute("aria-disabled", "true");
      if (tab.reason) {
        element.title = tab.reason;
      }
    }
    element.textContent = tab.label;
    container.appendChild(element);
  });
}
